import { useState, useEffect, useCallback } from 'react';
import { useGetOne, useUpdate, useNotify, useGetList } from 'react-admin';
import type { MutableRefObject } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  IconButton,
  Button,
  Alert,
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from '@mui/icons-material';
import { DashboardResources } from '@src/types/constants';
import type { VariantSkuDetails, VariantChangeImpact } from '@src/types';
import type { FacetGroup } from '@src/facets';
import { getVariantChangeImpact } from '@src/apis/reware/catalog';
import { requireAuth } from '@src/providers/dataProvider/shared/auth';
import { requirePartner } from '@src/providers/dataProvider/shared/partner';
import ArrayFieldEditor from './ArrayFieldEditor';
import TypedDataEditor from './TypedDataEditor';

export type VariantSaveRef = MutableRefObject<(() => Promise<void>) | null>;

interface VariantDetailPanelProps {
  sku: string;
  parentSku: string;
  variantSaveRef?: VariantSaveRef;
  onConfirmNeeded: (performSave: () => Promise<void>) => void;
  onImpactLoaded: (impact: VariantChangeImpact | null) => void;
}

// === Family Field (Standalone) ===

interface FamilyRow {
  id: number;
  tag: string;
  values: string[];
}

let familyRowId = 0;

function recordToRows(family: Record<string, unknown>): FamilyRow[] {
  return Object.entries(family).map(([tag, values]) => ({
    id: familyRowId++,
    tag,
    values: Array.isArray(values) ? values.filter((v): v is string => typeof v === 'string') : [],
  }));
}

function rowsToRecord(rows: FamilyRow[]): Record<string, string[]> {
  const record: Record<string, string[]> = {};
  for (const row of rows) {
    if (row.tag) {
      record[row.tag] = row.values;
    }
  }
  return record;
}

interface StandaloneFamilyFieldProps {
  value: Record<string, unknown>;
  onChange: (family: Record<string, unknown>) => void;
}

const StandaloneFamilyField = ({ value, onChange }: StandaloneFamilyFieldProps) => {
  const { data: facetGroups = [], isLoading, error } = useGetList<FacetGroup>('facets');
  const [rows, setRows] = useState<FamilyRow[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && value) {
      setRows(recordToRows(value));
      setInitialized(true);
    }
  }, [value, initialized]);

  const updateRows = useCallback((newRows: FamilyRow[]) => {
    setRows(newRows);
    onChange(rowsToRecord(newRows));
  }, [onChange]);

  const handleAddRow = useCallback(() => {
    updateRows([...rows, { id: familyRowId++, tag: '', values: [] }]);
  }, [rows, updateRows]);

  const handleRemoveRow = useCallback((id: number) => {
    updateRows(rows.filter(r => r.id !== id));
  }, [rows, updateRows]);

  const handleTagChange = useCallback((id: number, newTag: string | null) => {
    updateRows(rows.map(r => r.id === id ? { ...r, tag: newTag ?? '', values: [] } : r));
  }, [rows, updateRows]);

  const handleValuesChange = useCallback((id: number, newValues: string[]) => {
    updateRows(rows.map(r => r.id === id ? { ...r, values: newValues } : r));
  }, [rows, updateRows]);

  const getAvailableTagChoices = useCallback((currentRowId: number): string[] => {
    const usedTags = new Set(
      rows.filter(r => r.id !== currentRowId).map(r => r.tag).filter(Boolean)
    );
    return facetGroups.map(fg => fg.tag).filter(tag => !usedTags.has(tag));
  }, [rows, facetGroups]);

  const getValueChoices = useCallback((tag: string): string[] => {
    const facetGroup = facetGroups.find(fg => fg.tag === tag);
    return facetGroup?.values.map(v => v.name) ?? [];
  }, [facetGroups]);

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        Could not load facets. Family field editing is unavailable.
      </Alert>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Family
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Map facet tags to their values. Each tag can appear only once.
      </Typography>
      {isLoading ? (
        <Typography variant="body2" color="text.secondary">
          Loading facets...
        </Typography>
      ) : (
        <>
          {rows.map(row => (
            <Box
              key={row.id}
              sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', mb: 1.5 }}
            >
              <Autocomplete
                sx={{ flex: 1 }}
                size="small"
                options={getAvailableTagChoices(row.id)}
                value={row.tag || null}
                onChange={(_, newValue) => handleTagChange(row.id, newValue)}
                renderInput={params => (
                  <TextField {...params} label="Facet Tag" placeholder="Select a tag" />
                )}
              />
              <Autocomplete
                sx={{ flex: 2 }}
                size="small"
                multiple
                options={getValueChoices(row.tag)}
                value={row.values}
                onChange={(_, newValues) => handleValuesChange(row.id, newValues)}
                disabled={!row.tag}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Values"
                    placeholder={row.tag ? 'Select values' : 'Select a tag first'}
                  />
                )}
              />
              <IconButton
                size="small"
                onClick={() => handleRemoveRow(row.id)}
                color="error"
                title="Remove this facet"
                sx={{ mt: 0.5 }}
              >
                <RemoveCircleOutlineIcon />
              </IconButton>
            </Box>
          ))}
          <Button
            size="small"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddRow}
            sx={{ mt: 1 }}
          >
            Add Facet
          </Button>
        </>
      )}
    </Box>
  );
};

// === Main VariantDetailPanel ===

const VariantDetailPanel = ({ sku, parentSku, variantSaveRef, onConfirmNeeded, onImpactLoaded }: VariantDetailPanelProps) => {
  const notify = useNotify();
  const [update] = useUpdate();

  // Fetch variant details
  const { data: variantData, isLoading: isFetching } = useGetOne<VariantSkuDetails>(
    DashboardResources.VariantSkus,
    { id: sku, meta: { parentSku } },
    { enabled: !!sku }
  );

  // Form state
  const [upc, setUpc] = useState<string[]>([]);
  const [mpn, setMpn] = useState<string[]>([]);
  const [gtin, setGtin] = useState<string[]>([]);
  const [partnerSku, setPartnerSku] = useState<string[]>([]);
  const [partnerParentSku, setPartnerParentSku] = useState<string[]>([]);
  const [family, setFamily] = useState<Record<string, unknown>>({});
  const [data, setData] = useState<Record<string, unknown>>({});

  // Track whether form state has been initialized from fetched data
  const [formInitialized, setFormInitialized] = useState(false);

  // Original values for change detection
  const [originalFamily, setOriginalFamily] = useState<string>('{}');
  const [originalData, setOriginalData] = useState<string>('{}');

  // Initialize form from fetched data (only once — subsequent refetches must not
  // reset user edits or close an open confirmation dialog)
  useEffect(() => {
    if (variantData && !formInitialized) {
      setUpc(variantData.upc ?? []);
      setMpn(variantData.mpn ?? []);
      setGtin(variantData.gtin ?? []);
      setPartnerSku(variantData.partnerSku ?? []);
      setPartnerParentSku(variantData.partnerParentSku ?? []);
      setFamily(variantData.family ?? {});
      setData(variantData.data ?? {});
      setOriginalFamily(JSON.stringify(variantData.family ?? {}));
      setOriginalData(JSON.stringify(variantData.data ?? {}));
      setFormInitialized(true);
    }
  }, [variantData, formInitialized]);

  const performSave = useCallback(async () => {
    try {
      await update(
        DashboardResources.VariantSkus,
        {
          id: sku,
          data: { upc, mpn, gtin, partnerSku, partnerParentSku, family, data },
          previousData: variantData,
        },
        {
          mutationMode: 'pessimistic',
          onSuccess: () => {
            notify('Variant SKU updated', { type: 'success' });
            setOriginalFamily(JSON.stringify(family));
            setOriginalData(JSON.stringify(data));
          },
          onError: (error: Error) => {
            notify(`Failed to update variant: ${error.message}`, { type: 'error' });
          },
        }
      );
    } catch {
      // Error handled by onError callback
    }
  }, [sku, upc, mpn, gtin, partnerSku, partnerParentSku, family, data, variantData, update, notify]);

  const handleSave = useCallback(async () => {
    const familyChanged = JSON.stringify(family) !== originalFamily;
    const dataChanged = JSON.stringify(data) !== originalData;
    if (familyChanged || dataChanged) {
      onConfirmNeeded(performSave);
      try {
        const token = requireAuth();
        const currentPartner = requirePartner();
        const result = await getVariantChangeImpact({
          token,
          partnerCode: currentPartner.code,
          sku,
          ...(familyChanged ? { family } : {}),
          ...(dataChanged ? { data } : {}),
        });
        onImpactLoaded(result);
      } catch {
        // If impact fetch fails, still allow saving but show fallback UI
        onImpactLoaded(null);
      }
    } else {
      await performSave();
    }
  }, [family, data, originalFamily, originalData, sku, performSave, onConfirmNeeded, onImpactLoaded]);

  // Register save function so the parent toolbar can call it.
  // Cleanup on unmount so the toolbar doesn't invoke a stale handler
  // (e.g. when the user switches to a different tab).
  useEffect(() => {
    if (variantSaveRef) {
      variantSaveRef.current = handleSave;
    }
    return () => {
      if (variantSaveRef) {
        variantSaveRef.current = null;
      }
    };
  }, [variantSaveRef, handleSave]);

  if (isFetching || !formInitialized) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!variantData) {
    return (
      <Box py={4}>
        <Typography variant="body2" color="text.secondary">
          Failed to load variant details.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Variant Details
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {'SKU: '}
        {sku}
      </Typography>

      {/* Product Identifiers */}
      <ArrayFieldEditor label="UPC" values={upc} onChange={setUpc} />
      <ArrayFieldEditor label="MPN" values={mpn} onChange={setMpn} />
      <ArrayFieldEditor label="GTIN" values={gtin} onChange={setGtin} />
      <ArrayFieldEditor label="Partner SKU" values={partnerSku} onChange={setPartnerSku} />
      <ArrayFieldEditor label="Partner Parent SKU" values={partnerParentSku} onChange={setPartnerParentSku} />

      {/* Family Field */}
      <StandaloneFamilyField value={family} onChange={setFamily} />

      {/* Data Field */}
      <TypedDataEditor
        value={data}
        onChange={setData}
        definitions={variantData.catalogDataFieldDefinitions}
      />
    </Box>
  );
};

export default VariantDetailPanel;
